
'use client';

import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import CoinSparkline from './CoinSparkline';

const marketTabs = [
  { id: 'tab-top-coins', label: 'Top Coins' },
  { id: 'tab-top-gainers', label: 'Top Gainers' },
  { id: 'tab-top-losers', label: 'Top Losers' },
  { id: 'tab-trending', label: 'Trending' },
];

interface CoinQuote {
  id: number;
  name: string;
  symbol: string;
  price: number | null;
  change24h: number | null;
  marketCap: number | null;
  volume24h: number | null;
}

interface DisplayCoin {
  id: string;
  name: string;
  symbol: string;
  price: string;
  change: string;
  positive: boolean;
  sparkData: number[];
  iconBg: string;
  iconText: string;
}

const COIN_META: Record<string, { iconBg: string; iconText: string }> = {
  BTC: { iconBg: '#F7931A', iconText: '₿' },
  ETH: { iconBg: '#627EEA', iconText: 'Ξ' },
  SOL: { iconBg: '#9945FF', iconText: '◎' },
  BNB: { iconBg: '#F3BA2F', iconText: 'B' },
  XRP: { iconBg: '#00AAE4', iconText: 'X' },
  ADA: { iconBg: '#0033AD', iconText: 'A' },
  DOGE: { iconBg: '#C2A633', iconText: 'Ð' },
  AVAX: { iconBg: '#E84142', iconText: 'A' },
  DOT: { iconBg: '#E6007A', iconText: 'D' },
  LINK: { iconBg: '#2A5ADA', iconText: '⬡' },
  MATIC: { iconBg: '#8247E5', iconText: 'P' },
  ARB: { iconBg: '#28A0F0', iconText: 'A' },
  OP: { iconBg: '#FF0420', iconText: 'O' },
  NEAR: { iconBg: '#00C08B', iconText: 'N' },
  FIL: { iconBg: '#0090FF', iconText: 'F' },
  ICP: { iconBg: '#29ABE2', iconText: 'I' },
  ATOM: { iconBg: '#2E3148', iconText: 'A' },
  PEPE: { iconBg: '#00A550', iconText: 'P' },
  WIF: { iconBg: '#FF6B35', iconText: 'W' },
  FLOKI: { iconBg: '#FF8C00', iconText: 'F' },
  BONK: { iconBg: '#FF4500', iconText: 'B' },
};

const TOP_COINS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP'];
const TRENDING_SYMBOLS = ['PEPE', 'WIF', 'FLOKI', 'BONK', 'DOGE'];

// Safely convert API values into numbers.
function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

// Format prices without crashing on null or invalid data.
function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price) || price < 0) {
    return '—';
  }

  if (price >= 1000) {
    return (
      '$' +
      price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  if (price >= 1) return '$' + price.toFixed(2);
  if (price >= 0.01) return '$' + price.toFixed(4);

  return '$' + price.toFixed(8);
}

// Format percentage changes safely.
function formatChange(change: number | null | undefined): string {
  if (change == null || !Number.isFinite(change)) {
    return '—';
  }

  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

// Normalize API records before rendering them.
function normalizeCoin(value: unknown): CoinQuote | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const raw = value as Record<string, unknown>;

  if (
    typeof raw.name !== 'string' ||
    typeof raw.symbol !== 'string' ||
    raw.symbol.trim() === ''
  ) {
    return null;
  }

  const idValue = toNumber(raw.id);

  return {
    id: idValue ?? 0,
    name: raw.name,
    symbol: raw.symbol.trim().toUpperCase(),
    price: toNumber(raw.price),
    change24h: toNumber(raw.change24h),
    marketCap: toNumber(raw.marketCap),
    volume24h: toNumber(raw.volume24h),
  };
}

function buildDisplayCoin(coin: CoinQuote): DisplayCoin {
  const meta = COIN_META[coin.symbol] ?? {
    iconBg: '#888',
    iconText: coin.symbol[0] ?? '?',
  };

  const change = coin.change24h;
  const hasChange = change != null && Number.isFinite(change);

  return {
    id: `coin-${coin.symbol.toLowerCase()}`,
    name: coin.name,
    symbol: coin.symbol,
    price: formatPrice(coin.price),
    change: formatChange(change),
    positive: hasChange ? change >= 0 : true,
    sparkData: [],
    iconBg: meta.iconBg,
    iconText: meta.iconText,
  };
}

export default function MarketOverviewCard() {
  const [activeTab, setActiveTab] = useState('tab-top-coins');
  const [allCoins, setAllCoins] = useState<CoinQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const res = await fetch('/api/crypto/market-data');

        if (!res.ok) {
          throw new Error(`Market API returned ${res.status}`);
        }

        const json: unknown = await res.json();

        if (
          typeof json !== 'object' ||
          json === null ||
          !Array.isArray((json as { coins?: unknown }).coins)
        ) {
          throw new Error('Invalid market-data response');
        }

        const rawCoins = (json as { coins: unknown[] }).coins;

        const validCoins = rawCoins
          .map(normalizeCoin)
          .filter((coin): coin is CoinQuote => coin !== null);

        if (!cancelled) {
          setAllCoins(validCoins);
          setLoadError(false);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(true);
          console.error('Failed to fetch crypto market data:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    const interval = setInterval(fetchData, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const getCoinsForTab = (): DisplayCoin[] => {
    switch (activeTab) {
      case 'tab-top-coins':
        return TOP_COINS
          .map((symbol) => allCoins.find((coin) => coin.symbol === symbol))
          .filter((coin): coin is CoinQuote => coin !== undefined)
          .map(buildDisplayCoin);

      case 'tab-top-gainers':
        return [...allCoins]
          .filter(
            (coin) =>
              coin.change24h != null &&
              Number.isFinite(coin.change24h)
          )
          .sort((a, b) => b.change24h! - a.change24h!)
          .slice(0, 5)
          .map(buildDisplayCoin);

      case 'tab-top-losers':
        return [...allCoins]
          .filter(
            (coin) =>
              coin.change24h != null &&
              Number.isFinite(coin.change24h)
          )
          .sort((a, b) => a.change24h! - b.change24h!)
          .slice(0, 5)
          .map(buildDisplayCoin);

      case 'tab-trending':
        return TRENDING_SYMBOLS
          .map((symbol) => allCoins.find((coin) => coin.symbol === symbol))
          .filter((coin): coin is CoinQuote => coin !== undefined)
          .map(buildDisplayCoin);

      default:
        return [];
    }
  };

  const coins = getCoinsForTab();

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border">
        <h2 className="text-base font-semibold text-foreground">
          Market Overview
        </h2>

        <Link
          href="/markets"
          className="flex items-center gap-1 text-xs text-crypto-green hover:text-crypto-green/80 font-medium transition-colors duration-150"
        >
          View All Markets <ArrowRight size={13} />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        {marketTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-3 py-2.5 text-xs font-medium transition-colors duration-150 border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-crypto-green border-crypto-green'
                : 'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
        <div className="col-span-5">Coin</div>
        <div className="col-span-3 text-right">Price</div>
        <div className="col-span-2 text-right">24H %</div>
        <div className="col-span-2 text-right">Chart (7D)</div>
      </div>

      {/* Table Rows */}
      <div className="flex-1 divide-y divide-border/50">
        {coins.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">
            {loading
              ? 'Loading live data…'
              : loadError
                ? 'Unable to load market data. Please try again shortly.'
                : 'No market data available for this tab.'}
          </div>
        ) : (
          coins.map((coin) => (
            <div
              key={coin.id}
              className="grid grid-cols-12 items-center px-4 py-2.5 hover:bg-muted/30 transition-colors duration-150 cursor-pointer"
            >
              {/* Coin info */}
              <div className="col-span-5 flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: coin.iconBg }}
                >
                  {coin.iconText}
                </div>

                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-semibold text-foreground truncate">
                    {coin.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {coin.symbol}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="col-span-3 text-right">
                <span className="text-xs font-semibold text-foreground tabular-nums font-mono">
                  {coin.price}
                </span>
              </div>

              {/* Change */}
              <div className="col-span-2 text-right">
                <span
                  className={`text-xs font-semibold tabular-nums ${
                    coin.positive
                      ? 'text-crypto-green'
                      : 'text-crypto-red'
                  }`}
                >
                  {coin.change}
                </span>
              </div>

              {/* Sparkline */}
              <div className="col-span-2 flex justify-end">
                <CoinSparkline
                  data={coin.sparkData}
                  positive={coin.positive}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border">
        <Link
          href="/markets"
          className="flex items-center justify-center gap-1.5 text-xs text-crypto-green hover:text-crypto-green/80 font-medium transition-colors duration-150"
        >
          View All Markets <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
